import Dialog from '@mui/material/Dialog';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  addSymbolsToBoard,
  fetchBoard,
  fetchBoardList
} from '../../actions/marketboardRegistration';
import dropdownTriangleIcon from '../../icons/Icon-Dropdown-Triangle.svg';
import closeIcon from '../../icons/Icon-Modal-Close.svg';
import useLocalize from '../../utils/hooks/useLocalize';
import { getMaxSymbolsPerBoard } from '../../utils/marketboards';
import InlineErrorMessage from '../common/InlineErrorMessage';
import './AddToMarketBoard.scss';

const MAX_SYMBOLS_PER_BOARD = getMaxSymbolsPerBoard();

// MUI passes its positioning and open-state classes through `className`.
const DropdownTriangle = ({ className }) => (
  <img
    src={dropdownTriangleIcon}
    alt=""
    aria-hidden="true"
    width="12"
    height="10"
    className={classNames('add-to-board-select-icon', className)}
  />
);

DropdownTriangle.propTypes = {
  className: PropTypes.string
};

const toSymbolArray = (symbol) => {
  if (Array.isArray(symbol)) {
    return symbol;
  }
  return symbol ? [symbol] : [];
};

// A board counts as usable only if the *whole* selection fits. The AI Scanner
// registers several symbols at once (#730) and `addSymbolsToBoard` builds the
// full list before it writes, so a board with room for some but not all of
// them rejects the save outright rather than adding what fits — greying it out
// here is what keeps that from happening behind a live 「登録」 button.
// Coerced rather than trusted. This is an addition, so a `numSymbols` that
// arrived as the string "40" would concatenate instead of adding and read as
// wildly over capacity; absent or unparseable, it would make the sum NaN, and
// every comparison against NaN is false — which on this side of the test
// reads as "no room" and greys out a board with space to spare. The `<`
// comparison this replaced coerced numerically on its own and needed neither.
const boardSymbolCount = (candidate) => {
  const count = Number((candidate || {}).numSymbols);
  return Number.isFinite(count) ? count : 0;
};

const hasSpaceFor = (candidate, needed) =>
  boardSymbolCount(candidate) + needed <= MAX_SYMBOLS_PER_BOARD;

const findFirstWithSpace = (list, needed) =>
  (list || []).find((candidate) => hasSpaceFor(candidate, needed));

/**
 * The two-step "register a symbol to a Market Board" flow: the board-selection
 * modal (Figma 5271:12303) followed by the confirmation modal (5271:12435).
 *
 * The entry point is supplied per surface — this component only owns the
 * modals — so the AI Scanner results screen can reuse the same flow with its
 * own trigger and its own `classes` overrides.
 *
 * @param {object} props Component props.
 * @param {boolean} props.open Whether the flow is showing.
 * @param {Function} props.onClose Called when either modal is dismissed.
 * @param {string|Array<string>} props.symbol Symbol id(s) to register. A board
 *   is offered only when the whole set fits, so passing several narrows the
 *   selectable boards further than passing one. Read once per opening, so a
 *   surface may pass a live value without it shifting under the member.
 * @param {string} [props.className] Class on both dialog roots and on the
 *   board dropdown's menu, so a surface can scope its overrides to its own
 *   instance. All three are separate portals at the end of <body>.
 * @param {object} [props.classes] Per-surface class-name overrides.
 * @returns {JSX.Element|null} The active modal, or null while closed.
 */
export const AddToMarketBoardModal = ({
  open,
  onClose,
  symbol,
  className,
  classes = {}
}) => {
  const dispatch = useDispatch();
  const localize = useLocalize();
  const liveSymbols = useMemo(() => toSymbolArray(symbol), [symbol]);

  // Frozen for the life of one opening. A surface's selection can move while
  // the flow is on screen — the AI Scanner's candidate list is rebuilt by
  // background scan refreshes with no member action — and both the capacity
  // maths and the write have to use the set the member confirmed, not
  // whatever happens to be current when they tap 「登録」. Holding it steady
  // also keeps `needed` below from re-running the reset effect mid-flow and
  // throwing away a confirmation that has already been earned.
  const [openedWith, setOpenedWith] = useState(liveSymbols);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setOpenedWith(liveSymbols);
    }
  }
  const symbols = openedWith;

  // Floored at 1 so an empty selection never reads as "fits anywhere" and
  // leaves a full board selectable. A number, not an array — so as a
  // dependency of the effect below it compares by value (`Object.is`) and a
  // re-render that rebuilds the same selection re-triggers nothing.
  const needed = Math.max(symbols.length, 1);
  // Unique per instance so two mounted triggers cannot collide on the id the
  // dialog points `aria-labelledby` at.
  const headingId = useId();
  // #737: the target of the errors' `aria-describedby`. One id for both
  // messages, because at most one of them is ever mounted (a list failure
  // means no board, which keeps `handleRegister` from running) — so the id is
  // never ambiguous and the two anchors below cannot both resolve at once.
  const errorId = useId();

  const [boards, setBoards] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [board, setBoard] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingBoards, setIsLoadingBoards] = useState(false);
  const [registeredBoard, setRegisteredBoard] = useState(null);
  // #737 AC C20108250 / C20108251: the two failure states STORY-014 (#723)
  // deferred. Held separately rather than as one "error" string because they
  // are reached independently and mean different things to the member — the
  // list failing leaves nothing to register to, so the advice is to close and
  // reopen; the write failing leaves the modal usable, so the advice is to
  // retry in place.
  const [boardListError, setBoardListError] = useState(false);
  const [registerError, setRegisterError] = useState(false);

  // Bumped on every fetch so a slow response that lands after the member has
  // already changed board (or closed the modal) is discarded instead of
  // overwriting the current selection's detail.
  const requestId = useRef(0);
  const listRequestId = useRef(0);
  // Last board list seen, so reopening the modal is not another blank round
  // trip. Cleared after a register because `numSymbols` has changed.
  const boardListCache = useRef(null);

  const loadBoard = useCallback(
    (boardId) => {
      const id = (requestId.current += 1);
      setBoard(null);
      return dispatch(fetchBoard(boardId))
        .then((detail) => {
          if (requestId.current === id) {
            setBoard(detail);
          }
        })
        .catch(() => {
          if (requestId.current === id) {
            setBoard(null);
          }
        });
    },
    [dispatch]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    const listId = (listRequestId.current += 1);
    setIsSaving(false);
    setRegisteredBoard(null);
    setIsLoadingBoards(true);
    // #737: cleared on every opening alongside the rest of the per-session
    // state, so the previous opening's failure is not what greets the member
    // on the retry it told them to make.
    setBoardListError(false);
    setRegisterError(false);

    const cached = boardListCache.current;
    const preselected = cached ? findFirstWithSpace(cached, needed) : null;
    setBoards(cached || []);
    // The pick is reset on every opening, not only when the cache is empty.
    // What fits depends on the size of *this* selection, so a board chosen
    // for a smaller one must never survive into an opening it no longer has
    // room for — leaving it selected would keep 「登録」 live over a save that
    // can only fail.
    setSelectedId(preselected ? preselected.Id : '');
    setBoard(null);
    if (preselected) {
      loadBoard(preselected.Id);
    }

    dispatch(fetchBoardList())
      .then((list) => {
        if (listRequestId.current !== listId) {
          return;
        }
        boardListCache.current = list;
        setBoards(list);
        setIsLoadingBoards(false);

        const preselectionHolds =
          preselected &&
          list.some(
            (candidate) =>
              candidate.Id === preselected.Id && hasSpaceFor(candidate, needed)
          );
        if (preselectionHolds) {
          return;
        }

        const firstWithSpace = findFirstWithSpace(list, needed);
        if (firstWithSpace) {
          setSelectedId(firstWithSpace.Id);
          loadBoard(firstWithSpace.Id);
        } else {
          // Unconditionally, not just when something was pre-selected: with
          // no board able to take the selection there is nothing valid to
          // leave selected, whatever this opening or the last one chose.
          setSelectedId('');
          setBoard(null);
        }
      })
      .catch(() => {
        if (listRequestId.current !== listId) {
          return;
        }
        setIsLoadingBoards(false);
        // #737 AC C20108250: surfaced only when the failure actually leaves
        // the member with nothing. A cache from an earlier opening is still on
        // screen and still registerable — `fetchBoardList` failing on the
        // refresh means `numSymbols` may be a little stale, not that the
        // dropdown is empty — and telling them to close a modal that works
        // would be worse than saying nothing.
        if (!cached || cached.length === 0) {
          setBoardListError(true);
        }
      });
  }, [dispatch, loadBoard, needed, open]);

  const handleBoardChange = useCallback(
    (event) => {
      // #737: a different board is a different registration, so the previous
      // one's failure no longer applies. Dropped here rather than only in
      // `handleRegister` so the box goes as soon as the member reacts to it,
      // instead of sitting over a board it was never about.
      setRegisterError(false);
      setSelectedId(event.target.value);
      loadBoard(event.target.value);
    },
    [loadBoard]
  );

  const handleRegister = useCallback(() => {
    // An empty set is refused rather than written: `addSymbolsToBoard` would
    // happily PUT the board back with its symbols unchanged and resolve, so
    // the member would be shown 「新規登録されました」 for a registration that
    // added nothing. The surfaces all gate their trigger on a non-empty
    // selection, but that is their guard, not this component's.
    if (!board || isSaving || symbols.length === 0) {
      return;
    }
    // Same staleness guard as the two fetches: `listRequestId` is bumped on
    // every open, so a save the member abandoned by closing the modal cannot
    // surface its confirmation over the session they opened next.
    const saveId = listRequestId.current;
    setIsSaving(true);
    // #737: the previous attempt's error goes now rather than on settle, so a
    // retry does not leave "registration failed" on screen while the retry is
    // in flight.
    setRegisterError(false);
    dispatch(addSymbolsToBoard(board, symbols))
      .then((saved) => {
        // Cleared regardless of which session is on screen — the board really
        // was written, so every cached `numSymbols` is now stale.
        boardListCache.current = null;
        if (listRequestId.current !== saveId) {
          return;
        }
        setRegisteredBoard({ Id: board.Id, Name: saved.Name });
        setIsSaving(false);
      })
      .catch(() => {
        if (listRequestId.current !== saveId) {
          return;
        }
        setIsSaving(false);
        // #737 AC C20108251: `registeredBoard` is deliberately left null, so
        // the selection modal stays up and the confirmation modal is never
        // reached. `addSymbolsToBoard` rejects for 400/403/404/5xx and for a
        // timeout alike, so all five land here.
        setRegisterError(true);
      });
  }, [board, dispatch, isSaving, symbols]);

  if (!open) {
    return null;
  }

  const isBoardListPending = isLoadingBoards && boards.length === 0;

  const closeButton = (
    <button
      type="button"
      className={classNames('add-to-board-close', classes.closeButton)}
      aria-label={localize('button.label.close')}
      onClick={onClose}
    >
      <img src={closeIcon} alt="" aria-hidden="true" width="20" height="20" />
    </button>
  );

  if (registeredBoard) {
    return (
      <Dialog
        open
        onClose={onClose}
        className={classNames(
          'add-to-board-dialog',
          'add-to-board-dialog--confirmation',
          className
        )}
        aria-labelledby={headingId}
        PaperProps={{
          className: classNames('add-to-board-paper', classes.paper)
        }}
      >
        <div className="add-to-board-confirmation-head">
          <div className="add-to-board-close-row">{closeButton}</div>
          <p
            id={headingId}
            className={classNames('add-to-board-message', classes.message)}
          >
            {localize('marketboard.register.success', {
              board: (
                <Link
                  key="board"
                  className="add-to-board-message-link"
                  to={`/marketboard/${registeredBoard.Id}`}
                  onClick={onClose}
                >
                  {registeredBoard.Name}
                </Link>
              )
            })}
          </p>
        </div>
        <button
          type="button"
          className={classNames('add-to-board-confirm', classes.confirmButton)}
          onClick={onClose}
        >
          {localize('button.label.close')}
        </button>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      className={classNames(
        'add-to-board-dialog',
        'add-to-board-dialog--select',
        className
      )}
      aria-labelledby={headingId}
      // #737 AC C20108250: the list error is described on the DIALOG rather
      // than on a control, because it leaves no control to attach it to — the
      // dropdown is unmounted and 「登録」 is disabled, and a disabled button
      // is not focusable, so `aria-describedby` on it would never be read.
      // On the dialog it is announced whenever focus enters or returns to the
      // modal, which is the only thing the member can still act on.
      aria-describedby={boardListError ? errorId : undefined}
      PaperProps={{
        className: classNames('add-to-board-paper', classes.paper)
      }}
    >
      <div className="add-to-board-header">
        <h2
          id={headingId}
          className={classNames('add-to-board-title', classes.title)}
        >
          {localize('marketboard.register.title')}
        </h2>
        {closeButton}
      </div>
      {/*
        #737 AC C20108250: dropped entirely when the list failed, rather than
        left as an empty control. `boardListError` is only ever set with
        nothing cached to show (see the fetch's catch), so this can never hide
        a dropdown the member could have used — and an empty one offers them
        no action while telling them to close and try again.
      */}
      {!boardListError && (
        <Select
          className={classNames('add-to-board-select', classes.select)}
          value={selectedId}
          onChange={handleBoardChange}
          displayEmpty
          disabled={isBoardListPending}
          IconComponent={DropdownTriangle}
          inputProps={{ 'aria-label': localize('marketboard.register.title') }}
          // The menu is a portal of its own, sibling to the dialog rather than
          // inside it, so it needs the surface class too — a surface that has
          // restacked the dialog must be able to restack this with it.
          MenuProps={{
            className: classNames('add-to-board-select-menu', className)
          }}
        >
          {isBoardListPending && (
            <MenuItem value="">{localize('message.status.loading')}</MenuItem>
          )}
          {boards.map((candidate) => (
            <MenuItem
              key={candidate.Id}
              value={candidate.Id}
              disabled={!hasSpaceFor(candidate, needed)}
            >
              {candidate.Name}
            </MenuItem>
          ))}
        </Select>
      )}
      {/*
        #737: between the dropdown and the actions, so it sits next to the
        control the member would use next — the list error above a dropdown
        they cannot fix from here, the register error above the 「登録」 they
        would retry. At most one shows: the list error implies no board, which
        keeps `handleRegister` from ever running.
      */}
      {boardListError && (
        <InlineErrorMessage
          className="add-to-board-error"
          id={errorId}
          message={localize('marketboard.register.error.list')}
        />
      )}
      {registerError && (
        <InlineErrorMessage
          className="add-to-board-error"
          id={errorId}
          message={localize('marketboard.register.error.submit')}
        />
      )}
      <div className="add-to-board-actions">
        <button
          type="button"
          className={classNames('add-to-board-cancel', classes.cancelButton)}
          onClick={onClose}
        >
          {localize('button.label.cancel')}
        </button>
        <button
          type="button"
          className={classNames('add-to-board-submit', classes.submitButton)}
          disabled={!board || isSaving || symbols.length === 0}
          // #737 AC C20108251: unlike the list error, this one leaves the
          // button live and the retry is exactly what the member is being
          // asked to do — so it belongs on the button they will tab to, not
          // on the dialog.
          aria-describedby={registerError ? errorId : undefined}
          onClick={handleRegister}
        >
          {localize('marketboard.register.submit')}
        </button>
      </div>
    </Dialog>
  );
};

AddToMarketBoardModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  symbol: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  className: PropTypes.string,
  classes: PropTypes.object
};

export default AddToMarketBoardModal;
